import { Alert, AlertDescription } from "@nimit9/signet-ui/components/alert"
import { Button } from "@nimit9/signet-ui/components/button"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import type { CategoryType, Owner } from "@paisa/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { Amount } from "@/components/common/amount"
import { CategoryBadge } from "@/components/common/category-badge"
import { OwnerBadge } from "@/components/common/owner-badge"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useDateFormat } from "@/hooks/use-date-format"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"

export const Route = createFileRoute("/transactions/duplicates")({
  component: DuplicatesPage,
})

type DuplicateTransaction = {
  id: string
  date: string
  description: string
  amount: string
  type: "debit" | "credit"
  owner: Owner
  isDuplicate: boolean
  merchant?: { cleanName: string } | null
  category?: { name: string; type: string; icon?: string | null } | null
}

function DuplicatesPage() {
  const formatDate = useDateFormat()
  const qc = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ["transactions", "duplicates"],
    queryFn: () =>
      api.get<{
        success: boolean
        count: number
        truncated?: boolean
        data: DuplicateTransaction[]
      }>("/transactions/duplicates"),
  })

  const resolveMutation = useMutation({
    mutationFn: (body: { ids: string[]; action: "keep" | "ignore" }) =>
      api.post("/transactions/duplicates/resolve", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] })
      qc.invalidateQueries({ queryKey: ["dashboard"] })
      setSelectedIds(new Set())
    },
  })

  const dupes = data?.data ?? []
  const allSelected = dupes.length > 0 && dupes.every((d) => selectedIds.has(d.id))

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelectedIds(allSelected ? new Set() : new Set(dupes.map((d) => d.id)))
  }

  function resolve(action: "keep" | "ignore") {
    if (!selectedIds.size) return
    resolveMutation.mutate({ ids: Array.from(selectedIds), action })
  }

  return (
    <>
      <Header
        title="Duplicate Transactions"
        subtitle={dupes.length > 0 ? `${dupes.length} flagged` : undefined}
        showPeriod={false}
        action={
          <Link
            to="/transactions"
            className="text-[12px] text-text-muted hover:text-text-secondary"
          >
            ← Transactions
          </Link>
        }
      />
      <PageWrapper>
        {/* Explainer */}
        <div className="mb-4 rounded-[10px] border border-accent-amber/20 bg-accent-amber/5 px-4 py-3">
          <p className="text-[13px] text-text-primary">
            These transactions were flagged as possible duplicates during import.
          </p>
          <p className="mt-0.5 text-[12px] text-text-muted">
            <strong>Keep</strong> — treat as a real transaction (removes duplicate flag).{" "}
            <strong>Ignore</strong> — exclude from all calculations.
          </p>
        </div>

        {data?.truncated && (
          <Alert className="mb-4">
            <AlertDescription>
              Showing first {dupes.length} — resolve these or narrow the filters to see the rest.
            </AlertDescription>
          </Alert>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2">
            <span className="text-[12px] text-text-muted">{selectedIds.size} selected</span>
            <div className="ml-2 flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => resolve("keep")}
                disabled={resolveMutation.isPending}
              >
                Keep
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => resolve("ignore")}
                disabled={resolveMutation.isPending}
                className="text-accent-red hover:text-accent-red"
              >
                Ignore
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => i).map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-bg-surface" />
            ))}
          </div>
        )}

        {!isLoading && dupes.length === 0 && (
          <EmptyState
            title="No duplicate transactions"
            description="All transactions look clean — no duplicates were detected during import."
          />
        )}

        {!isLoading && dupes.length > 0 && (
          <div className="rounded-[10px] border border-border-subtle bg-bg-surface">
            {/* Header */}
            <div className="grid grid-cols-[32px_100px_1fr_140px_90px_110px] gap-2 border-b border-border-subtle px-4 py-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                aria-label="Select all duplicates"
                className="h-3.5 w-3.5 cursor-pointer rounded accent-accent-green self-center"
              />
              <span className="text-[11px] tracking-wider text-text-muted">Date</span>
              <span className="text-[11px] tracking-wider text-text-muted">Description</span>
              <span className="text-[11px] tracking-wider text-text-muted">Category</span>
              <span className="text-[11px] tracking-wider text-text-muted">Owner</span>
              <span className="text-right text-[11px] tracking-wider text-text-muted">Amount</span>
            </div>

            {dupes.map((txn) => {
              const isSelected = selectedIds.has(txn.id)
              const displayName = txn.merchant?.cleanName || txn.description.slice(0, 45)

              return (
                <div
                  key={txn.id}
                  onClick={() => toggleRow(txn.id)}
                  className={cn(
                    "grid cursor-pointer grid-cols-[32px_100px_1fr_140px_90px_110px] items-center gap-2 border-b border-border-subtle px-4 py-2.5 transition-colors last:border-b-0 hover:bg-bg-elevated/60",
                    isSelected && "bg-accent-green/[0.04]",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleRow(txn.id)
                    }}
                    aria-label={`Select ${displayName}`}
                    className="h-3.5 w-3.5 cursor-pointer rounded accent-accent-green"
                  />
                  <span className="text-[12px] text-text-muted">{formatDate(txn.date)}</span>

                  <div className="min-w-0">
                    <p className="truncate text-[13px] text-text-primary">
                      {txn.category?.icon && <span className="mr-1.5">{txn.category.icon}</span>}
                      {displayName}
                    </p>
                    {txn.merchant && (
                      <p className="truncate text-[11px] text-text-muted">
                        {txn.description.slice(0, 50)}
                      </p>
                    )}
                  </div>

                  <div>
                    {txn.category ? (
                      <CategoryBadge
                        type={txn.category.type as CategoryType}
                        label={txn.category.name}
                      />
                    ) : (
                      <span className="text-[11px] text-text-hint">—</span>
                    )}
                  </div>

                  <div>
                    <OwnerBadge owner={txn.owner} />
                  </div>

                  <div className="text-right">
                    <Amount
                      value={
                        txn.type === "debit" ? -parseFloat(txn.amount) : parseFloat(txn.amount)
                      }
                      showSign
                      className="text-[13px] font-medium"
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Quick actions at bottom when all resolved */}
        {!isLoading && dupes.length > 0 && (
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedIds(new Set(dupes.map((d) => d.id)))
              }}
            >
              Select all
            </Button>
          </div>
        )}
      </PageWrapper>
    </>
  )
}
